import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  previewAutoOrderApproval,
  approveAutoOrders,
} from "../../../distflowAPI/ordersApi";

function message(error) {
  if (error?.response?.status === 404)
    return "Approval is not available on the connected backend yet. Update the backend to use this action.";
  const flatten = (value) => {
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return value.map(flatten).join(" ");
    return Object.values(value || {})
      .map(flatten)
      .join(" ");
  };
  return (
    flatten(error?.response?.data) ||
    "Could not complete this request. Please try again."
  );
}

export default function ApproveAutoOrdersDialog({ date, onClose, onApproved }) {
  const [selectedDate, setSelectedDate] = useState(date);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const minimumDate = dayjs().add(1, "day").format("YYYY-MM-DD");
  const dateIsValid =
    /^\d{4}-\d{2}-\d{2}$/.test(selectedDate) && selectedDate >= minimumDate;
  const formattedDate = dayjs(selectedDate).format("MMM D, YYYY");

  async function loadPreview() {
    setBusy("preview");
    setError("");
    setPreview(null);
    try {
      const { data } = await previewAutoOrderApproval(selectedDate);
      setPreview(data);
    } catch (error) {
      setError(message(error));
    } finally {
      setBusy("");
    }
  }
  async function approve() {
    setBusy("approve");
    setError("");
    try {
      const { data } = await approveAutoOrders(
        selectedDate,
        preview.preview_token
      );
      onApproved(data);
    } catch (error) {
      setError(message(error));
      setPreview(null);
    } finally {
      setBusy("");
    }
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={busy ? undefined : onClose}
      aria-labelledby="approve-auto-orders-title"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle id="approve-auto-orders-title" sx={{ pb: 1 }}>
        Approve auto orders
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Approve automatic orders across all accounts for one delivery date.
            The current table filters do not limit this action.
          </Typography>
          <TextField
            label="Order date"
            type="date"
            fullWidth
            value={selectedDate}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minimumDate }}
            disabled={!!busy}
            helperText="Choose the date the orders are for."
            onChange={(event) => {
              setSelectedDate(event.target.value);
              setPreview(null);
              setError("");
            }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
          {busy === "preview" ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">
                Checking orders for {formattedDate}…
              </Typography>
            </Stack>
          ) : null}
          {preview ? (
            <>
              {preview.order_count ? (
                <Box
                  sx={{
                    p: 2.5,
                    border: "1px solid #dce2f4",
                    bgcolor: "#f5f7ff",
                    borderRadius: 1.5,
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    Ready for approval · {formattedDate}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {preview.order_count}{" "}
                    {preview.order_count === 1 ? "order" : "orders"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {preview.item_count} order{" "}
                    {preview.item_count === 1 ? "item" : "items"} will be
                    approved.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    Automatic orders will be created where needed. This also
                    approves pending items in existing automatic orders.
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info">
                  No auto orders are waiting for approval on this date.
                </Alert>
              )}
              {preview.blocked?.length ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {preview.blocked.length}{" "}
                    {preview.blocked.length === 1
                      ? "order needs"
                      : "orders need"}{" "}
                    individual review
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, maxHeight: 160, overflowY: "auto" }}
                  >
                    {preview.blocked.map((row, index) => (
                      <Typography
                        component="li"
                        variant="body2"
                        key={index}
                        sx={{ mb: 1 }}
                      >
                        <strong>{row.account}:</strong> {row.reason}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Manual orders and canceled items are excluded. Customer
                confirmation emails are not sent by this action.
              </Typography>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2.5,
          flexWrap: "wrap",
          gap: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button disabled={!!busy} onClick={onClose}>
          Cancel
        </Button>
        {preview?.order_count ? (
          <Button variant="contained" disabled={!!busy} onClick={approve}>
            {busy === "approve"
              ? "Approving…"
              : `Approve ${preview.order_count} ${preview.order_count === 1 ? "order" : "orders"} for ${formattedDate}`}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!!busy || !dateIsValid}
            onClick={loadPreview}
          >
            {busy === "preview" ? "Checking…" : "Preview orders"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
